import {noop} from 'lodash';
import {createContext, useContext} from 'react';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CollapsedItemStates} from '../../../../../helpers/expansionTogglingHelpers';
import {ParentTagOverridesMap} from '../../../../../helpers/visualCustomViewHelpers';
import {SearchTagModel} from '../../../../../models/api/searchTagModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {VisualWorkspaceModel} from '../../../../../models/api/visualWorkspaceModel';
import {WorkspaceFolderRootLabelsEnum} from '../../../../../models/api/workspaceModel';
import {
  WorkspaceDropReorderLensHandler,
  WorkspaceDropReorderTagHandler,
  WorkspaceDropReorderTeamInboxHandler,
  WorkspaceDropReorderTeammateHandler,
  WorkspaceDropReorderTeammateWorkspaceItemHandler,
  WorkspaceDropReorderTeamViewHandler,
  WorkspaceTagDropNestHandler,
} from '../../../../../store/data/workspace/reorder/types';
import {
  createEmptyVisualWorkspace,
  TagModelMap,
  TeamInboxModelMap,
  TeammateModelMap,
} from '../../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {WorkspaceChangeId} from '../../../../../store/data/workspace/visualWorkspace/visualWorkspaceUpdateHelpers';
import {
  ToggleDropdownTeamCustomViewHandler,
  ToggleDropdownTeamInboxHandler,
  ToggleDropdownTeammateHandler,
  ToggleExpandItemHandler,
  ToggleExpandTagHandler,
  ToggleTeamCustomViewHandler,
  ToggleTeamInboxHandler,
  WorkspacesEditorDialogsEnum,
} from './types';

/*
 * Props.
 */

export type ToggleDropdownTagHandler = (
  searchTag: SearchTagModel,
  parentTagIdsToAdd: number[],
  childTagIdsToRemove: number[],
) => void;

export interface ToggleTagParams {
  tag: TagModel;
  allParentTags: readonly TagModel[];
  allChildTags: readonly TagModel[];
  ifUsedHelpTip: boolean;
}
export type ToggleTagHandler = (params: ToggleTagParams) => void;

export type ToggleTeammateHandler = (id: WorkspaceChangeId, ifUsedHelpTip: boolean) => void;

export type ToggleLensHandler = (id: WorkspaceChangeId, ifUsedHelpTip: boolean) => void;
export type LensUpdateHandler = (id: WorkspaceChangeId) => void;

export type TagCreatedHandler = (tag: TagModel, parentTags: readonly TagModel[]) => void;

export interface WorkspacesEditorContextProps {
  draftWorkspace: VisualWorkspaceModel;
  collapsedItems: CollapsedItemStates;
  isSaveDisabled: boolean;
  visibleDialog: WorkspacesEditorDialogsEnum | null;
  onToggleRootFolder: HandlerOf<WorkspaceFolderRootLabelsEnum>;
  onToggleTag: ToggleTagHandler;
  onToggleTeammate: ToggleTeammateHandler;
  onToggleDropdownTeammate: ToggleDropdownTeammateHandler;
  onToggleTeamInbox: ToggleTeamInboxHandler;
  onToggleDropdownTeamInbox: ToggleDropdownTeamInboxHandler;
  onToggleTeamCustomView: ToggleTeamCustomViewHandler;
  onToggleLens: ToggleLensHandler;
  onCreateLens: LensUpdateHandler;
  onToggleDropdownTeamCustomView: ToggleDropdownTeamCustomViewHandler;
  onToggleExpandSection: ToggleExpandItemHandler;
  onToggleExpandTag: ToggleExpandTagHandler;
  onDismissDialog: Handler;
  onDismissEditor: Handler;
  onRequestCancelChanges: Handler;
  onRequestSaveChanges: Handler;
  onSaveChanges: Handler;
  shouldReloadTagResults: boolean;
  onReloadTagResults: Handler;
  onReloadTagResultsDone: Handler;
  onToggleDropdownTag: ToggleDropdownTagHandler;
  tagModelMap: TagModelMap;
  teamInboxModelMap: TeamInboxModelMap;
  teamCustomViewModelMap: TagModelMap;
  teammateModelMap: TeammateModelMap;
  parentTagOverridesMap: ParentTagOverridesMap;
  onDropNestTag: WorkspaceTagDropNestHandler;
  onDropReorderTag: WorkspaceDropReorderTagHandler;
  onDropReorderNestedFolder: WorkspaceDropReorderTeammateWorkspaceItemHandler;
  onDropReorderTeamInbox: WorkspaceDropReorderTeamInboxHandler;
  onDropReorderTeamView: WorkspaceDropReorderTeamViewHandler;
  onDropReorderTeammate: WorkspaceDropReorderTeammateHandler;
  onDropReorderLens: WorkspaceDropReorderLensHandler;
  onTagCreated: TagCreatedHandler;
  didBlockNavigation: boolean;
  isSavingWorkspaces: boolean;
}

/*
 * Context.
 */

export const WorkspacesEditorContext = createContext<WorkspacesEditorContextProps>({
  draftWorkspace: createEmptyVisualWorkspace(),
  collapsedItems: {},
  isSaveDisabled: true,
  visibleDialog: null,
  onToggleRootFolder: noop,
  onToggleTag: noop,
  onToggleTeammate: noop,
  onToggleDropdownTeammate: noop,
  onToggleTeamCustomView: noop,
  onToggleDropdownTeamCustomView: noop,
  onToggleLens: noop,
  onCreateLens: noop,
  onToggleExpandSection: noop,
  onToggleExpandTag: noop,
  onToggleTeamInbox: noop,
  onToggleDropdownTeamInbox: noop,
  onDismissDialog: noop,
  onDismissEditor: noop,
  onRequestCancelChanges: noop,
  onRequestSaveChanges: noop,
  onSaveChanges: noop,
  shouldReloadTagResults: false,
  onReloadTagResults: noop,
  onReloadTagResultsDone: noop,
  tagModelMap: {},
  teamInboxModelMap: {},
  teamCustomViewModelMap: {},
  onToggleDropdownTag: noop,
  teammateModelMap: {},
  parentTagOverridesMap: {},
  onDropNestTag: noop,
  onDropReorderTag: noop,
  onDropReorderNestedFolder: noop,
  onDropReorderTeamInbox: noop,
  onDropReorderTeamView: noop,
  onDropReorderTeammate: noop,
  onDropReorderLens: noop,
  onTagCreated: noop,
  didBlockNavigation: false,
  isSavingWorkspaces: false,
});

export function useWorkspacesEditor() {
  return useContext(WorkspacesEditorContext);
}
